<template>
  <div v-if="article" class="article-form">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Edit Article: {{ article.name }}</h4>
      <router-link :to="{ name: 'articles-list' }" class="btn btn-default btn-sm">Back to list</router-link>
    </div>

    <div class="article-form__layout">
      <!-- Main content -->
      <div class="article-form__main">
        <card title="Content" class="article-form__card">
          <div class="row mb-3">
            <div class="col-md-12">
              <label>Title *</label>
              <el-input v-model="form.name" placeholder="Article title" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <label>H1 (page heading)</label>
              <el-input v-model="form.h1" placeholder="Leave empty to reuse the title as H1" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <label>Short description (lead under the H1)</label>
              <el-input v-model="form.short_description" type="textarea" :rows="3" maxlength="320" show-word-limit placeholder="One or two plain-text sentences" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <label>Meta description</label>
              <el-input v-model="form.meta_description" type="textarea" :rows="3" maxlength="320" show-word-limit placeholder="SEO meta description for search and social previews" />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-2">
              <label>Language</label>
              <el-select v-model="form.lang" disabled style="width: 100%">
                <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
              </el-select>
            </div>
            <div class="col-md-10">
              <label>Alias</label>
              <el-input v-model="form.alias" placeholder="article-alias" disabled />
            </div>
          </div>
          <div class="article-form__editor-wrap mb-3">
            <label>Text *</label>
            <ckeditor
              :key="`text-${editorRenderKey}`"
              v-model="form.text"
              :config="ckeditorConfig"
            ></ckeditor>
          </div>

          <div class="mb-3">
            <label>Key takeaways</label>
            <div v-for="(item, index) in form.key_takeaways" :key="index" class="d-flex align-items-start mb-2">
              <el-input :value="item" type="textarea" :autosize="{ minRows: 1 }" placeholder="One takeaway, plain text" class="mr-2" @input="v => $set(form.key_takeaways, index, v)" />
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="form.key_takeaways.splice(index, 1)" />
            </div>
            <el-button size="mini" @click="form.key_takeaways.push('')">Add takeaway</el-button>
            <small class="text-muted d-block mt-1">Rendered as the "Key takeaways" bullet list; an empty list hides the block.</small>
          </div>

          <div class="mb-3">
            <label>Table of contents</label>
            <div v-for="(item, index) in form.table_of_contents" :key="index" class="d-flex align-items-start mb-2">
              <div class="flex-grow-1 mr-2">
                <el-select v-model="item.anchor" filterable placeholder="Target H2 anchor" style="width: 100%" class="mb-1">
                  <el-option v-for="heading in tocHeadings" :key="heading.anchor" :label="`${heading.title}  (#${heading.anchor})`" :value="heading.anchor" />
                </el-select>
                <el-input v-model="item.title" type="textarea" :autosize="{ minRows: 1 }" placeholder="Link label (defaults to the H2 title)" />
              </div>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="form.table_of_contents.splice(index, 1)" />
            </div>
            <el-button size="mini" @click="form.table_of_contents.push({ title: '', anchor: '' })">Add section</el-button>
            <small class="text-muted d-block mt-1">Each entry links to an existing <code>&lt;h2 id="…"&gt;</code> in the article text. An empty list hides the block.</small>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label>Draft Title</label>
              <el-input v-model="form.draft.title" placeholder="Draft title" />
            </div>
          </div>

          <div class="article-form__editor-wrap mb-3">
            <label>Draft Text</label>
            <ckeditor
              :key="`draft-text-${editorRenderKey}`"
              v-model="form.draft.text"
              :config="ckeditorConfigDraftText"
            ></ckeditor>
          </div>

          <div class="article-form__editor-wrap mb-3">
            <label>References</label>
            <ckeditor
              :key="`references-${editorRenderKey}`"
              v-model="form.references"
              :config="ckeditorConfigReferences"
            ></ckeditor>
          </div>
        </card>

        <!-- Images -->
        <card title="Images" class="article-form__card">
          <p class="small text-muted">Listing images — shown in the blog list and as the article preview.</p>
          <div class="row">
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Main image (full width)</p>
              <small class="text-muted d-block mb-2">JPG/PNG, fitted into 1200×450</small>
              <img v-if="mainImagePreview" :src="mainImagePreview" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.fileMain.click()">Upload</button>
              </div>
              <input type="file" ref="fileMain" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'main')" style="display:none" />
              <small v-if="fileMain" class="text-success">Selected: {{ fileMain.name }}</small>
            </div>
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Preview image (thumbnail)</p>
              <small class="text-muted d-block mb-2">JPG/PNG, fitted into 290×210</small>
              <img v-if="previewImagePreview" :src="previewImagePreview" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.filePreview.click()">Upload</button>
              </div>
              <input type="file" ref="filePreview" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'preview')" style="display:none" />
              <small v-if="filePreview" class="text-success">Selected: {{ filePreview.name }}</small>
            </div>
          </div>
          <hr />
          <p class="small text-muted">Article header images (new /article/ design). Optional — the header renders without an image if empty.</p>
          <div class="row">
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Header — vertical (desktop)</p>
              <small class="text-muted d-block mb-2">Portrait, fitted into 900×1200; sits right of the title on desktop</small>
              <img v-if="headerVerticalPreview" :src="headerVerticalPreview" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.fileHeaderVertical.click()">Upload</button>
              </div>
              <input type="file" ref="fileHeaderVertical" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'header_vertical')" style="display:none" />
              <small v-if="fileHeaderVertical" class="text-success">Selected: {{ fileHeaderVertical.name }}</small>
            </div>
            <div class="col-md-6 text-center mb-3">
              <p class="small text-muted mb-1">Header — horizontal (mobile)</p>
              <small class="text-muted d-block mb-2">Landscape, fitted into 1200×675; sits under the author block on mobile</small>
              <img v-if="headerHorizontalPreview" :src="headerHorizontalPreview" class="article-form__img mb-2" />
              <div>
                <button type="button" class="article-form__upload-btn" @click.prevent="$refs.fileHeaderHorizontal.click()">Upload</button>
              </div>
              <input type="file" ref="fileHeaderHorizontal" accept="image/jpeg,image/png" @change="handleFileUpload($event, 'header_horizontal')" style="display:none" />
              <small v-if="fileHeaderHorizontal" class="text-success">Selected: {{ fileHeaderHorizontal.name }}</small>
            </div>
          </div>
        </card>
      </div>

      <!-- Sidebar -->
      <div class="article-form__sidebar">
        <card title="Relations" class="article-form__card">
          <label>Author</label>
          <el-select v-model="form.author" value-key="id" filterable clearable placeholder="Select author" style="width: 100%" class="mb-2">
            <el-option v-for="u in users" :key="u.id" :label="`${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`" :value="u" />
          </el-select>

          <label>Editor</label>
          <el-select v-model="form.editor" value-key="id" filterable clearable placeholder="Select editor" style="width: 100%" class="mb-2">
            <el-option v-for="u in users" :key="u.id" :label="`${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`" :value="u" />
          </el-select>

          <label>Illness Kind</label>
          <el-select v-model="form.illness_kind" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in illnessKinds" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Illness</label>
          <el-select v-model="form.illness" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in illnesses" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Operation</label>
          <el-select v-model="form.operation" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in operations" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Diagnostic</label>
          <el-select v-model="form.diagnostic" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in diagnostics" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Country</label>
          <el-select v-model="form.country" value-key="id" filterable clearable placeholder="Select" style="width: 100%" class="mb-2">
            <el-option v-for="item in countries" :key="item.id" :label="item.name && item.name.en || item.name && item.name.ru || item.id" :value="item" />
          </el-select>

          <label>Tags</label>
          <el-select v-model="form.tags" value-key="id" multiple filterable placeholder="Select tags" style="width: 100%" class="mb-2">
            <el-option v-for="tag in tags" :key="tag.id" :label="tag.name && tag.name.en || tag.id" :value="tag" />
          </el-select>
        </card>

        <card title="Settings" class="article-form__card">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span>Active</span>
            <el-switch v-model="form.active" active-color="#a3cc0e" inactive-color="#f96332" />
          </div>
        </card>

        <card title="Translations" v-if="form.translations && form.translations.length" class="article-form__card">
          <div class="mb-2">
            <router-link
              v-for="t in form.translations"
              :key="t"
              :to="{ name: 'articles-edit', params: { id: t } }"
              class="d-block small mb-1"
              target="_blank"
            >{{ t }}</router-link>
          </div>
        </card>

        <card title="Translate to" class="article-form__card">
          <el-checkbox-group v-model="translateTo">
            <el-checkbox v-for="l in langs" :key="l" :label="l" :disabled="l === form.lang || existingTranslationLangs.includes(l)">
              {{ l.toUpperCase() }}
            </el-checkbox>
          </el-checkbox-group>
        </card>

        <div class="mt-3">
          <el-button type="primary" :loading="saving" @click="save" style="width: 100%">Save Changes</el-button>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="text-center py-5">
    <span>Loading...</span>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import APIArticles from "@/api/modules/articles";

export default {
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      ckeditorConfig: { allowedContent: true },
      ckeditorConfigDraftText: { allowedContent: true },
      ckeditorConfigReferences: { allowedContent: true },
      editorRenderKey: 0,
      saving: false,
      translateTo: [],
      fileMain: null,
      filePreview: null,
      fileHeaderVertical: null,
      fileHeaderHorizontal: null,
      fileMainDataUrl: null,
      filePreviewDataUrl: null,
      fileHeaderVerticalDataUrl: null,
      fileHeaderHorizontalDataUrl: null,
      form: {
        id: "",
        lang: "",
        name: "",
        h1: "",
        short_description: "",
        meta_description: "",
        key_takeaways: [],
        table_of_contents: [],
        alias: "",
        text: "",
        references: "",
        active: true,
        author: null,
        editor: null,
        illness_kind: null,
        illness: null,
        operation: null,
        diagnostic: null,
        country: null,
        tags: [],
        translations: [],
        image: { main: "", preview: "", header_vertical: "", header_horizontal: "" },
        draft: { title: "", text: "" },
      },
    };
  },
  computed: {
    ...mapState("articles", ["article", "tags", "users"]),
    ...mapState("cmsApiCatalogs", {
      illnessKinds: "illness-kinds",
      illnesses: "illnesses",
      operations: "operations",
      diagnostics: "diagnostics",
      countries: "countries",
      domains: "domains",
    }),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    existingTranslationLangs() {
      return (this.form.translations || []).map(t => t.split(":")[0]);
    },
    mainImagePreview() {
      return this.fileMainDataUrl || this.form.image.main || "";
    },
    previewImagePreview() {
      return this.filePreviewDataUrl || this.form.image.preview || "";
    },
    headerVerticalPreview() {
      return this.fileHeaderVerticalDataUrl || this.form.image.header_vertical || "";
    },
    headerHorizontalPreview() {
      return this.fileHeaderHorizontalDataUrl || this.form.image.header_horizontal || "";
    },
    tocHeadings() {
      const headings = [];
      const seen = new Set();
      const regex = /<h2\b[^>]*\bid\s*=\s*(["'])([^"']+)\1[^>]*>([\s\S]*?)<\/h2>/gi;
      let match;
      while ((match = regex.exec(this.form.text || "")) !== null) {
        const anchor = match[2].trim();
        if (!anchor || seen.has(anchor)) continue;
        const title = match[3].replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
        headings.push({ anchor, title });
        seen.add(anchor);
      }
      return headings;
    },
  },
  methods: {
    ...mapActions("articles", ["getArticle", "upsertArticle", "translateArticle", "getTags", "getUsers"]),
    ...mapActions("cmsApiCatalogs", ["getIllnessKinds", "getIllnesses", "getOperations", "getDiagnostics", "getCountries", "getDomains"]),
    parseId(id) {
      const lang = id.substring(0, 2);
      const alias = id.substring(3);
      return { lang, alias };
    },
    populateForm() {
      const a = this.article;
      if (!a) return;
      const { lang, alias } = this.parseId(a.id || this.id);
      this.form.id = a.id || `${lang}:${alias}`;
      this.form.lang = lang;
      this.form.alias = alias;
      this.form.name = a.name || "";
      this.form.h1 = a.h1 || "";
      this.form.short_description = a.short_description || "";
      this.form.meta_description = a.meta_description || "";
      this.form.key_takeaways = Array.isArray(a.key_takeaways) ? [...a.key_takeaways] : [];
      this.form.table_of_contents = Array.isArray(a.table_of_contents) ? a.table_of_contents.map(item => typeof item === "string" ? { title: item, anchor: "" } : { title: item && item.title || "", anchor: item && item.anchor || "" }) : [];
      this.form.text = a.text || "";
      this.form.references = a.references || "";
      this.form.active = !a.hide;
      this.form.author = a.author ? { ...a.author } : null;
      this.form.editor = a.editor ? { ...a.editor } : null;
      this.form.illness_kind = a.illness_kind ? { ...a.illness_kind } : null;
      this.form.illness = a.illness ? { ...a.illness } : null;
      this.form.operation = a.operation ? { ...a.operation } : null;
      this.form.diagnostic = a.diagnostic ? { ...a.diagnostic } : null;
      this.form.country = a.country ? { ...a.country } : null;
      this.form.tags = Array.isArray(a.tags) ? [...a.tags] : [];
      this.form.translations = Array.isArray(a.translations) ? [...a.translations] : [];
      this.form.image = this.imageFromArticle(a);
      this.form.draft = {
        title: a.draft && a.draft.title || "",
        text: a.draft && a.draft.text || "",
        created_at: a.draft && a.draft.created_at || null,
        updated_at: a.draft && a.draft.updated_at || null,
      };
      this.editorRenderKey += 1;
    },
    imageFromArticle(a) {
      const img = (a && a.image) || {};
      return {
        main: img.main || "",
        preview: img.preview || "",
        header_vertical: img.header_vertical || "",
        header_horizontal: img.header_horizontal || "",
      };
    },
    /** Upload slot per blog-api image type: [File data key, preview data-URL key]. */
    fileSlot(type) {
      return {
        main: ["fileMain", "fileMainDataUrl"],
        preview: ["filePreview", "filePreviewDataUrl"],
        header_vertical: ["fileHeaderVertical", "fileHeaderVerticalDataUrl"],
        header_horizontal: ["fileHeaderHorizontal", "fileHeaderHorizontalDataUrl"],
      }[type];
    },
    selectedImageFiles() {
      const files = {};
      ["main", "preview", "header_vertical", "header_horizontal"].forEach(type => {
        const [fileKey] = this.fileSlot(type);
        if (this[fileKey] instanceof File) files[type] = this[fileKey];
      });
      return files;
    },
    handleFileUpload(event, type) {
      const file = event.target.files[0];
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        return alert("Only .jpg, .jpeg, .png files are allowed");
      }
      const [fileKey, dataUrlKey] = this.fileSlot(type);
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        this[dataUrlKey] = reader.result;
        this[fileKey] = file;
      });
      reader.readAsDataURL(file);
    },
    async uploadImages() {
      // blog-api uploads whichever types are sent and keeps the stored URL for the rest,
      // so any subset (just the header pair, just a new preview, ...) is a valid upload.
      const files = this.selectedImageFiles();
      if (!Object.keys(files).length) return;

      try {
        await APIArticles.addImage({ lang: this.form.lang, articleAlias: this.form.alias, files });
        this.$notify({ type: "success", title: "Images uploaded" });
        Object.keys(files).forEach(type => {
          const [fileKey, dataUrlKey] = this.fileSlot(type);
          this[fileKey] = null;
          this[dataUrlKey] = null;
        });
        // Reload article to get new image URLs
        const { lang, alias } = this.parseId(this.id);
        await this.getArticle({ lang, alias });
        this.form.image = this.imageFromArticle(this.article);
      } catch (err) {
        this.$notify({ type: "warning", title: "Image upload failed" });
        console.error("Image upload error:", err);
      }
    },
    formatAuthor(author) {
      if (!author || !author.id) return null;
      return {
        id: author.id,
        email: author.email || "",
        first_name: author.firstName || author.first_name || "",
        last_name: author.lastName || author.last_name || "",
        metadata: author.metadata || {},
        phone: author.phone || "",
        avatar: author.avatar || "",
      };
    },
    formatCountry(country) {
      if (!country) return null;
      return { ...country, iso_code: country.code || country.iso_code || "" };
    },
    formatIllnessKind(ik) {
      if (!ik) return null;
      return { alias: ik.alias, id: parseInt(ik.id), name: ik.name };
    },
    normalizedTableOfContents() {
      return (this.form.table_of_contents || []).map(item => ({ title: ((item && item.title) || "").trim(), anchor: ((item && item.anchor) || "").trim() })).filter(item => item.title || item.anchor);
    },
    validateTableOfContents(items) {
      if (items.some(item => !item.title || !item.anchor)) {
        this.$notify({ type: "warning", title: "Each table-of-contents entry needs a label and a target H2 anchor" });
        return false;
      }
      const anchors = new Set(this.tocHeadings.map(item => item.anchor));
      if (items.some(item => !anchors.has(item.anchor))) {
        this.$notify({ type: "warning", title: "Each table-of-contents anchor must target an H2 id in the article text" });
        return false;
      }
      if (new Set(items.map(item => item.anchor)).size !== items.length) {
        this.$notify({ type: "warning", title: "A table-of-contents target can only be used once" });
        return false;
      }
      return true;
    },
    async save() {
      if (!this.form.name) {
        return this.$notify({ type: "warning", title: "Title is required" });
      }
      const tableOfContents = this.normalizedTableOfContents();
      if (!this.validateTableOfContents(tableOfContents)) return;
      this.saving = true;
      try {
        const text = this.form.text || "";
        const references = this.form.references || "";
        const draft = {
          title: this.form.draft && this.form.draft.title || "",
          text: this.form.draft && this.form.draft.text || "",
        };

        // Handle new translations
        const newLangs = this.translateTo.filter(l => !this.existingTranslationLangs.includes(l) && l !== this.form.lang);
        if (newLangs.length) {
          await this.translateArticle({ lang: this.form.lang, alias: this.form.alias, targetLocales: newLangs });
          this.$notify({ type: "success", title: "Translations sent" });
        }

        const payload = {
          id: this.form.id || `${this.form.lang}:${this.form.alias}`,
          name: this.form.name,
          h1: (this.form.h1 || "").trim(),
          short_description: (this.form.short_description || "").trim(),
          meta_description: (this.form.meta_description || "").trim(),
          key_takeaways: (this.form.key_takeaways || []).map(t => (t || "").trim()).filter(Boolean),
          table_of_contents: tableOfContents,
          text,
          hide: !this.form.active,
          references,
          author: this.formatAuthor(this.form.author),
          editor: this.formatAuthor(this.form.editor),
          illness_kind: this.formatIllnessKind(this.form.illness_kind),
          illness: this.form.illness || null,
          operation: this.form.operation || null,
          diagnostic: this.form.diagnostic || null,
          country: this.formatCountry(this.form.country),
          tags: this.form.tags,
          translations: this.form.translations,
          image: this.form.image,
          draft,
        };

        await this.upsertArticle(payload);
        this.$notify({ type: "success", title: "Article saved" });

        // Upload images separately (after successful upsert)
        await this.uploadImages();

        if (newLangs.length) {
          // Reload to get updated translations
          const { lang, alias } = this.parseId(this.id);
          await this.getArticle({ lang, alias });
          this.populateForm();
          this.translateTo = [];
        }
      } catch (e) {
        this.$notify({ type: "warning", title: "Error saving article" });
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    this.$store.dispatch("setLoader", true);
    const { lang, alias } = this.parseId(this.id);
    await Promise.all([
      this.getDomains(),
      this.getArticle({ lang, alias }),
      this.getTags(),
      this.getUsers(),
      this.getIllnessKinds({ perPage: 1500 }),
      this.getIllnesses({ perPage: 1500 }),
      this.getOperations({ perPage: 1500 }),
      this.getDiagnostics({ perPage: 1500 }),
      this.getCountries({ perPage: 1500 }),
    ]);
    this.populateForm();
    this.$store.dispatch("setLoader", false);
  },
};
</script>

<style scoped>
.article-form__layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.article-form__card {
  border-radius: 12px;
}
.article-form__editor-wrap {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px;
  background: #fcfdff;
}
.article-form__editor-wrap label {
  display: inline-block;
  margin-bottom: 10px;
  font-weight: 600;
}
.article-form__main {
  flex: 1;
  min-width: 0;
}
.article-form__sidebar {
  width: 320px;
  flex-shrink: 0;
}
.article-form__img {
  max-width: 100%;
  max-height: 150px;
  border-radius: 8px;
  object-fit: contain;
}
.article-form__upload-btn {
  display: inline-block;
  padding: 8px 20px;
  background: #f4f8fb;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}
.article-form__upload-btn:hover {
  background: #e9ecef;
}
</style>
